import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { format, startOfDay } from 'date-fns';
import { vi } from 'date-fns/locale';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { buildQuickDates, WEEKDAY_CHIP } from './quick-dates';
import { CALENDAR_CLASSNAMES, CHIP } from './search-bar.styles';
import { useSearchBarContext } from './use-search-bar';

interface DatePanelProps {
  /** `popover` keeps the calendar always visible; `sheet` collapses it behind a disclosure. */
  variant: 'popover' | 'sheet';
  className?: string;
}

export function DatePanel({ variant, className }: DatePanelProps) {
  const { dateValue, selectedDate, pickDate } = useSearchBarContext();

  // Captured once per mount. Safe despite being a clock read in a component:
  // this panel only ever mounts inside a portal opened by a user gesture, so it
  // is never part of the SSR payload and there is nothing to mismatch.
  const [today] = useState(() => startOfDay(new Date()));
  const [quickDates] = useState(() => buildQuickDates(startOfDay(new Date())));

  // Open by default on both variants. The sheet used to collapse it to keep the
  // surface short, but the date now owns a whole step — collapsed, that step is a
  // chip rail and an empty screen. The toggle stays so it can still be folded away
  // on a short viewport.
  const [isCalendarOpen, setIsCalendarOpen] = useState(true);

  // The form deliberately starts with no date so the server's own "after 16:00,
  // default to tomorrow" rule can apply when the user never touches this panel.
  // Once they open it, though, showing nothing selected is just a dead rail — so
  // today is pre-selected here, on the client, after the gesture.
  useEffect(() => {
    if (!dateValue) pickDate(today);
    // Runs once on mount; re-running on every dateValue change would fight the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div role="group" aria-label="Ngày gợi ý" className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-pl-4 px-4">
        {quickDates.map((quick) => (
          <button
            key={quick.value}
            type="button"
            aria-pressed={quick.value === dateValue}
            onClick={() => pickDate(quick.date)}
            className={cn(CHIP.base)}
          >
            <span className={CHIP.label}>{quick.label}</span>
            <span className={CHIP.sub}>{quick.sub}</span>
          </button>
        ))}
      </div>

      {variant === 'sheet' && (
        <button
          type="button"
          onClick={() => setIsCalendarOpen((open) => !open)}
          aria-expanded={isCalendarOpen}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-primary-ink flex items-center gap-1.5 self-start rounded-md py-1 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none"
        >
          <CalendarDays className="size-4" />
          <span>{isCalendarOpen ? 'Thu gọn lịch' : 'hoặc chọn ngày khác'}</span>
          <ChevronDown
            className={cn('size-4 transition-transform duration-200 motion-reduce:transition-none', isCalendarOpen && 'rotate-180')}
          />
        </button>
      )}

      {isCalendarOpen && (
        // Without this, a horizontal drag across the calendar is read by vaul as a
        // dismiss gesture and the sheet closes mid-interaction.
        <div data-vaul-no-drag className="rounded-card border-pill-border border p-1">
          <Calendar
            mode="single"
            locale={vi}
            selected={selectedDate}
            onSelect={(date) => date && pickDate(date)}
            defaultMonth={selectedDate ?? today}
            // A Matcher object, not a comparator. The old code compared the RHF
            // string value against a Date, which is always false via NaN.
            disabled={{ before: today }}
            formatters={{
              formatCaption: (date) => format(date, "'Tháng' M 'năm' yyyy", { locale: vi }),
              formatWeekdayName: (date) => WEEKDAY_CHIP[date.getDay()],
            }}
            classNames={CALENDAR_CLASSNAMES}
            className="w-full [--cell-size:--spacing(9)]"
          />
        </div>
      )}
    </div>
  );
}
